import {
  IDObject,
  HeaderObject,
  FoldersObject,
  BlocksObject,
  TagsObject,
  ToggleProperty,
  StatusObject,
} from 'types';

/**
 * The point type.
 * @type {number}
 */
export type Point = number;

/**
 * The enum of estimate types.
 * @param {'credit'} Credit
 * @param {'five'} Five
 * @param {'ten'} Ten
 */
export enum EstimateType {
  Credit = 'credit',
  Five = 'five',
  Ten = 'ten',
}

/**
 * The interface of object with task settings.
 * @param {settings} settings
 */
export interface TaskSettingsObject {

  /**
   * The task settings.
   */
  settings: {

    /**
     * The time limit of the task
     * @type {limit | undefined} limit
     */
    limit?: ToggleProperty<{

      /**
       * The limit minutes.
       * @type {number}
       */
      minutes: number;

    }>;

    /**
     * The attempts of the task.
     * @type {attempts | undefined} attempts
     */
    attempts?: ToggleProperty<{

      /**
       * The attempts count.
       * @type {number}
       */
      count: number;

    }>;

    /**
     * Required estimate of the task.
     * @type {estimate} estimate
     */
    estimate: {

      /**
       * The estimate type.
       * @type {EstimateType}
       */
      type: EstimateType;

      /**
       * Required estimate points.
       * @type {Point[]} points
       */
      points: Point[];

      /**
       * The estimate total credit.
       * @type {number | undefined}
       */
      totalCredit?: number;

    };

  }

}

/**
 * The interface of a test answer block.
 * @param {boolean} isTrue
 * @param {string} text
 * @param {string | undefined} note
 */
export interface TestAnswerBlock {

  /**
   * Is the answer true.
   * @type {boolean}
   */
  isTrue: boolean;

  /**
   * Text the answer.
   * @type {string}
   */
  text: string;

  /**
   * Note of the answer.
   * @type {string | undefined}
   */
  note?: string;

}

/**
 * The interface of a info object.
 * @param {string} question
 * @param {string} description
 */
export interface InfoObject {

  /**
   * Required question of the block.
   * @type {string}
   */
  question: string;

  /**
   * Required description of the block.
   * @type {string | undefined}
   */
  description?: string;

}

/**
 * The interface of a comment object.
 * @param {comment | undefined} comment
 */
export interface CommentObject {

  /**
   * The comment message of the block.
   * @type {comment | undefined} comment
   */
  comment?: ToggleProperty<{

    /**
     * The fail comment message.
     * @type {string}
     */
    fail: string;

    /**
     * The success comment message.
     * @type {string}
     */
    success: string;

  }>;

}

/**
 * The enum with block types.
 * @param {'test'} Test
 * @param {'autoCheck'} AutoCheck
 * @param {'freeAnswer'} FreeAnswer
 * @param {'accordance'} Accordance
 */
export enum BlockType {
  Test = 'test',
  AutoCheck = 'autoCheck',
  FreeAnswer = 'freeAnswer',
  Classification = 'Classification',
  Accordance = 'accordance',
}

/**
 * The interface of a block object.
 * @type {IDObject}
 * @type {InfoObject}
 * @param {BlockType} type
 * @type {T extends object}
 * @type {C extends object}
 */
export type BlockObject<T extends string, C extends object> =
  & IDObject
  & InfoObject
  & CommentObject
  & {

  /**
   * The type of the block.
   */
  type: T;

  /**
   * The score of the block.
   */
  score: Point;

} & C;

/**
 * The type of test block object.
 * @param {boolean | undefined} isSeveral
 * @param {TestAnswerBlock[]} answers
 * @param {comment | undefined} comment
 */
export type TestBlockObject = BlockObject<BlockType.Test, {

  /**
   * Is several answers in this block.
   * @type {boolean | undefined}
   */
  isSeveral?: boolean;

  /**
   * Required answers of the block.
   * @type {answers} answers
   */
  answers: TestAnswerBlock[];

}>;

/**
 * The type of a auto check block object.
 * @property {string[]} answers
 */
export type AutoCheckBlockObject = BlockObject<BlockType.AutoCheck, {

  /**
   * The answers of the block.
   */
  answers: string[];

}>;

/**
 * The type of a free answer block object.
 * @type {InfoObject}
 */
export type FreeAnswerBlockObject =
  BlockObject<BlockType.FreeAnswer, InfoObject>;

/**
 * The interface of a classification group object.
 * @param {string} title
 * @param {string[]} words
 */
export interface ClassificationGroupObject {

  /**
   * Title of the group.
   * @type {string}
   */
  title: string;

  /**
   * Words in the group.
   * @type {string[]}
   */
  words: string[];

}

/**
 * The type of a classification block object.
 * @param {ClassificationGroupObject[]} groups
 */
export type ClassificationBlockObject = BlockObject<BlockType.Classification, {

  /**
   * The groups of the block.
   * @type {ClassificationGroupObject[]}
   */
  groups: ClassificationGroupObject[];

}>;

/**
 * The type of a accordance block object.
 * @param {[string, string][]} pairs
 */
export type AccordanceBlockObject = BlockObject<BlockType.Accordance, {

  /**
   * The pairs of the block.
   */
  pairs: [string, string][];

}>;

/**
 * The interface of a task content object.
 * @param {BlocksObject} content
 */
export interface TaskContentObject {

  content:
    BlocksObject<
      | TestBlockObject
      | AutoCheckBlockObject
      | FreeAnswerBlockObject
      | ClassificationBlockObject
      | AccordanceBlockObject
    >
  ;

}

/**
 * The task progress type.
 * @param {'pending'} Pending
 * @param {'success'} Success
 * @param {'fail'} Fail
 */
export enum TaskProgressType {
  Pending = 'pending',
  Success = 'success',
  Fail = 'fail',
}

/**
 * The interface of a task progress object.
 * @param {progress | undefined} progress
 */
export interface TaskProgressObject {

  progress?: {

    /**
     * Progress status.
     * @type {TaskProgressType}
     */
    status: TaskProgressType;

  };

}

/**
 * The type of a task object.
 * @type {IDObject}
 * @type {HeaderObject}
 * @type {FoldersObject}
 * @type {TagsObject}
 * @type {TaskSettingsObject}
 * @type {TaskContentObject}
 */
export type TaskObject =
  & IDObject
  & HeaderObject
  & StatusObject<'draft' | 'private' | 'public' | 'ban'>
  & FoldersObject
  & TagsObject
  & TaskSettingsObject
  & TaskProgressObject
  & TaskContentObject
;

/**
 * The type of a database exercises object.
 */
export type ExercisesObject =
  & IDObject
  & HeaderObject
  & StatusObject<'draft' | 'private' | 'public' | 'ban'>
  & TagsObject
  & {

    timeLimit: {
      isActive: boolean;
      minutes: number;
    };

    attemptsConfig: {
      isActive:	boolean;
      count: number;
    };

    gradeOptions: {
      gradeSystem: 'credit' | 'five-points' | 'ten-points';
      creditMinimum: number;
      count: number;
      levels: string[];
    };

    items: string[];

  }
;
